import type { ReactNode } from "react";

// 0 is here because Stack has it and callers reasonably expect the two scales to
// match — a Row of items that butt together is as legitimate as a Stack of them.
type Gap = 0 | 0.5 | 1 | 1.5 | 2 | 2.5 | 3 | 4 | 5 | 6;
type Align = "start" | "center" | "end" | "stretch" | "baseline";

interface RowProps {
  gap?: Gap;
  wrap?: boolean;
  justify?: "start" | "center" | "end" | "between";
  align?: Align;
  /**
   * Let this Row become narrower than its contents. Off by default because
   * that's the flexbox rule: a Row nested inside another flex container
   * refuses to shrink past its content and overflows the parent instead — so
   * anything inside it that truncates never gets narrow enough to do so.
   *
   * Not on by default: shrinking below the content means unshrinkable content
   * (an image, an unbreakable string) overflows and draws over its neighbour,
   * which is the worse failure when it happens. Set it when the Row holds
   * something that can truncate or wrap. Note it must be set at *every* level
   * of a nested flex chain — an ancestor without it will silently stop a
   * descendant from shrinking.
   */
  shrinkable?: boolean;
  /**
   * Take the free space in the parent flex container, rather than shrink-wrapping
   * to the content. Stack has always had this and Row hasn't, which is a trap:
   * without it a Row can't stand in for a `w-full … justify-between` div, because
   * it shrink-wraps and `justify` then silently does nothing.
   */
  grow?: boolean;
  children: ReactNode;
}

const gapClasses: Record<Gap, string> = {
  0: "gap-0",
  0.5: "gap-0.5",
  1: "gap-1",
  1.5: "gap-1.5",
  2: "gap-2",
  2.5: "gap-2.5",
  3: "gap-3",
  4: "gap-4",
  5: "gap-5",
  6: "gap-6",
};

const justifyClasses: Record<string, string> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
};

const alignClasses: Record<Align, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline",
};

export function Row({
  gap = 2,
  wrap,
  justify,
  align = "center",
  shrinkable,
  grow,
  children,
}: RowProps) {
  const classes = [
    "flex",
    alignClasses[align],
    gapClasses[gap],
    wrap && "flex-wrap",
    justify && justifyClasses[justify],
    shrinkable && "min-w-0",
    grow && "flex-1",
  ]
    .filter(Boolean)
    .join(" ");

  return <div className={classes}>{children}</div>;
}
